// src/components/H3ControlPanel.js - Панель управления H3 слоем
import React, { useState, useEffect } from 'react';
import './H3ControlPanel.css';

function H3ControlPanel({ h3Layer, onSettingsChange }) {
  const [enabled, setEnabled] = useState(false);
  const [resolution, setResolution] = useState(8);
  const [showStats, setShowStats] = useState(false);
  const [stats, setStats] = useState(null);

  // 🔧 Управление включением/выключением
  const handleToggle = (newEnabled) => {
    setEnabled(newEnabled);
    if (h3Layer) {
      h3Layer.toggle(newEnabled);
    }
    onSettingsChange?.({ enabled: newEnabled, resolution });
  };

  // 🎯 Изменение разрешения
  const handleResolutionChange = (newResolution) => {
    setResolution(newResolution);
    if (h3Layer && enabled) {
      h3Layer.setResolution(newResolution);
    }
    onSettingsChange?.({ enabled, resolution: newResolution });
  };

  // 📊 Получение статистики
  const updateStats = async () => {
    if (!h3Layer || !enabled) {
      setStats(null);
      return;
    }

    // Здесь можно получить статистику от слоя
    const mockStats = {
      totalHexagons: Math.floor(Math.random() * 200) + 50,
      totalPOI: Math.floor(Math.random() * 5000) + 1000,
      averagePOIPerHex: 0,
      maxPOIInHex: Math.floor(Math.random() * 500) + 100,
      categories: {
        empty: Math.floor(Math.random() * 30) + 5,
        low: Math.floor(Math.random() * 50) + 20,
        medium: Math.floor(Math.random() * 40) + 15,
        high: Math.floor(Math.random() * 20) + 5,
        'very-high': Math.floor(Math.random() * 10) + 2
      }
    };

    mockStats.averagePOIPerHex = (mockStats.totalPOI / mockStats.totalHexagons).toFixed(1);
    setStats(mockStats);
  };

  // 🔄 Обновление статистики при изменениях
  useEffect(() => {
    if (enabled && showStats) {
      updateStats();
      const interval = setInterval(updateStats, 5000); // Обновляем каждые 5 сек
      return () => clearInterval(interval);
    }
  }, [enabled, showStats, resolution]);

  return (
    <div className="h3-control-panel">
      <div className="h3-control-header">
        <h3>🌍 H3 POI Density</h3>
        <button 
          className={`h3-toggle-btn ${enabled ? 'enabled' : 'disabled'}`}
          onClick={() => handleToggle(!enabled)}
        >
          {enabled ? '🟢 ON' : '🔴 OFF'}
        </button>
      </div>

      {enabled && (
        <div className="h3-control-content">
          {/* 🎯 Контроль разрешения */}
          <div className="h3-control-section">
            <label className="h3-control-label">
              Resolution: {resolution}
              <div className="h3-resolution-info">
                {getResolutionInfo(resolution)}
              </div>
            </label>
            <input
              type="range"
              min="6"
              max="12"
              value={resolution}
              onChange={(e) => handleResolutionChange(parseInt(e.target.value))}
              className="h3-resolution-slider"
            />
            <div className="h3-resolution-labels">
              <span>6 (Large)</span>
              <span>9 (Medium)</span>
              <span>12 (Small)</span>
            </div>
          </div>

          {/* 📊 Цветовая легенда */}
          <div className="h3-control-section">
            <h4>Color Legend</h4>
            <div className="h3-color-legend">
              <div className="legend-item">
                <div className="legend-color" style={{backgroundColor: '#1a1a2e'}}></div>
                <span>0 POI</span>
              </div>
              <div className="legend-item">
                <div className="legend-color" style={{backgroundColor: '#16213e'}}></div>
                <span>1-4 POI</span>
              </div>
              <div className="legend-item">
                <div className="legend-color" style={{backgroundColor: '#533483'}}></div>
                <span>10-19 POI</span>
              </div>
              <div className="legend-item">
                <div className="legend-color" style={{backgroundColor: '#7209b7'}}></div>
                <span>20-49 POI</span>
              </div>
              <div className="legend-item">
                <div className="legend-color" style={{backgroundColor: '#f72585'}}></div>
                <span>50-99 POI</span>
              </div>
              <div className="legend-item">
                <div className="legend-color" style={{backgroundColor: '#ff4081'}}></div>
                <span>200+ POI</span>
              </div>
            </div>
          </div>

          {/* 📈 Кнопка статистики */}
          <div className="h3-control-section">
            <button 
              className={`h3-stats-btn ${showStats ? 'active' : ''}`}
              onClick={() => setShowStats(!showStats)}
            >
              {showStats ? '📊 Hide Stats' : '📈 Show Stats'}
            </button>
          </div>

          {/* 📊 Панель статистики */}
          {showStats && stats && (
            <div className="h3-stats-panel">
              <h4>📊 Statistics</h4>
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-value">{stats.totalHexagons}</span>
                  <span className="stat-label">Total Hexagons</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{stats.totalPOI.toLocaleString()}</span>
                  <span className="stat-label">Total POI</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{stats.averagePOIPerHex}</span>
                  <span className="stat-label">Avg POI/Hex</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{stats.maxPOIInHex}</span>
                  <span className="stat-label">Max POI/Hex</span>
                </div>
              </div>

              <div className="density-categories">
                <h5>Density Categories:</h5>
                {Object.entries(stats.categories).map(([category, count]) => (
                  <div key={category} className="category-item">
                    <span className="category-name">{category}:</span>
                    <span className="category-count">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ℹ️ Информация */}
          <div className="h3-info-section">
            <details>
              <summary>ℹ️ About H3 POI Density</summary>
              <p>
                This layer shows the density of Points of Interest (POI) in each H3 hexagon.
                Higher hexagons indicate areas with more restaurants, shops, services, etc.
              </p>
              <ul>
                <li><strong>Height:</strong> Represents POI count</li>
                <li><strong>Color:</strong> Shows density category</li>
                <li><strong>Click:</strong> View hexagon details</li>
                <li><strong>Resolution 6-12:</strong> Larger to smaller hexagons</li>
              </ul>
            </details>
          </div>
        </div>
      )}
    </div>
  );
}

// 🎯 Информация о разрешении H3
function getResolutionInfo(resolution) {
  const info = {
    6: 'Large hexagons (~36 km²)',
    7: 'Large-medium hexagons (~5 km²)',
    8: 'Medium hexagons (~0.7 km²)',
    9: 'Medium-small hexagons (~0.1 km²)',
    10: 'Small hexagons (~0.015 km²)',
    11: 'Very small hexagons (~0.002 km²)',
    12: 'Tiny hexagons (~0.0003 km²)'
  };
  
  return info[resolution] || `Resolution ${resolution}`;
}

export default H3ControlPanel;